import React, { useState, useRef, useEffect } from "react";
import { useHistory } from "react-router-dom";
import { makeStyles } from "@material-ui/core/styles";
import {
  Stepper,
  Step,
  StepButton,
  Button,
  Container,
  Snackbar
} from "@material-ui/core";
import MuiAlert from "@material-ui/lab/Alert";
import { Send as SendIcon } from "@material-ui/icons";

import AssetUploaderManager from "../AssetUploader/UploadStep/AssetUploaderManager";
import AssetNaming from "../AssetUploader/NamingStep/AssetNaming";
import AssetReview from "../AssetUploader/ReviewStep/AssetReview";
import Confirmation from "../Shared/Confirmation/Confirmation";

import { useSelector } from "react-redux";

import { insertDefaultCreative } from "../../services/dcm/creatives";

const useStyles = makeStyles((theme) => ({
  root: {
    width: "100%"
  },
  backButton: {
    marginRight: theme.spacing(1)
  },
  instructions: {
    marginTop: theme.spacing(1),
    marginBottom: theme.spacing(1)
  },
  stepper: {
    margin: "20px auto 30px",
    width: "50%",
    paddingTop: 0
  }
}));

const next = {
  float: "right"
};

function getSteps() {
  return ["Upload default creative", "Generate name", "Review"];
}

const creativeTypes = ["DISPLAY"];

function getStepContent(stepIndex, props) {
  switch (stepIndex) {
    case 0:
      return (
        <div>
          <AssetUploaderManager
            isThumbnailEnabled={true}
            filetype={"image/*, .zip, video/*"}
            creativeTypes={creativeTypes}
            selectedCreatives={props.selectedCreatives}
            setSelectedCreatives={props.setSelectedCreatives}
            placements={props.placements}
            uploadLimit={1}
            ref={(r) => (props.stepRef.current = r)}
          />
        </div>
      );
    case 1:
      return (
        <div>
          <AssetNaming
            isClickEnabled={true}
            isDragDisabled={true}
            isThumbnailEnabled={true}
            ref={(r) => (props.stepRef.current = r)}
          />
        </div>
      );
    case 2:
      return (
        <div>
          <AssetReview
            isClickEnabled={false}
            isDragDisabled={true}
            isThumbnailEnabled={true}
            isDefault={true}
            ref={(r) => (props.stepRef.current = r)}
          />
        </div>
      );
    default:
      return " ";
  }
}

export default () => {
  const classes = useStyles();
  const [activeStep, setActiveStep] = useState(0);
  // Tracks an actual submission. Deriving "done" from
  // activeStep === steps.length is unsafe when the step count is derived
  // from creative state: if a step disappears while the user sits on a
  // later one, activeStep can land on steps.length and report a
  // successful submission that never happened.
  const [hasSubmitted, setHasSubmitted] = useState(false);
  const [completed, setCompleted] = useState(new Set());
  const [skipped, setSkipped] = useState(new Set());
  const [selectedCreatives, setSelectedCreatives] = useState([]);
  const bartCreatives = useSelector((state) => state.creatives);
  const dcmCampaigns = useSelector((state) => state.dcmCampaigns);
  const steps = getSteps();

  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarOpen, setSnackbarOpen] = useState(false);

  const stepRef = useRef();

  const history = useHistory();
  let dimensions = history.location.pathname.split("/default/")[1]; //after the split dimensions is the second element

  useEffect(() => {
    if (!dcmCampaigns.selectedId) {
      history.push("/");
    }
  }, [dcmCampaigns.selectedId, history]);

  const handleNext = () => {
    if (
      stepRef &&
      stepRef.current &&
      stepRef.current.hasOwnProperty("validate")
    ) {
      const validation = stepRef.current.validate();

      if (validation.hasErrors) {
        if (validation.message) {
          setSnackbarMessage(validation.message);
          setSnackbarOpen(true);
        }

        return;
      }
    }

    setActiveStep((prevActiveStep) => prevActiveStep + 1);
  };

  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };

  const handleReset = () => {
    setHasSubmitted(false);
    setActiveStep(0);
  };

  const handleStep = (step) => () => {
    // The Stepper header stays mounted on the Confirmation screen, so a
    // StepButton click has to drop out of it — otherwise activeStep moves
    // but Confirmation keeps rendering and the user is stuck there.
    setHasSubmitted(false);
    setActiveStep(step);
  };

  const isStepSkipped = (step) => {
    return skipped.has(step);
  };

  function isStepComplete(step) {
    return completed.has(step);
  }

  const handleTraffic = () => {
    insertDefaultCreative(
      Object.values(bartCreatives.byId),
      dcmCampaigns.byId[dcmCampaigns.selectedId],
      dimensions,
      "DISPLAY"
    );
    // Set before advancing: the Confirmation screen is gated on this,
    // not on the step index.
    setHasSubmitted(true);
    setActiveStep((prevActiveStep) => prevActiveStep + 1);
  };

  return (
    <div className={classes.root}>
      <Stepper
        alternativeLabel
        activeStep={activeStep}
        nonLinear
        className={classes.stepper}
      >
        {steps.map((label, index) => {
          const stepProps = {};
          const buttonProps = {};
          if (isStepSkipped(index)) {
            stepProps.completed = false;
          }
          return (
            <Step key={label} {...stepProps}>
              <StepButton
                onClick={handleStep(index)}
                completed={isStepComplete(index)}
                {...buttonProps}
              >
                {label}
              </StepButton>
            </Step>
          );
        })}
      </Stepper>
      <Container maxWidth="lg">
        <div>
          {hasSubmitted ? (
            <Confirmation handleResetParent={handleReset} />
          ) : (
            <div>
              <div style={next}>
                <Button
                  disabled={activeStep === 0}
                  onClick={handleBack}
                  className={classes.button}
                  disableElevation
                >
                  Back
                </Button>
                {activeStep === steps.length - 1 ? (
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={handleTraffic}
                    startIcon={<SendIcon />}
                    disableElevation
                  >
                    Submit Placements
                  </Button>
                ) : (
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={handleNext}
                    disableElevation
                  >
                    Next
                  </Button>
                )}
              </div>
              {getStepContent(activeStep, {
                stepRef,
                selectedCreatives,
                setSelectedCreatives,
                placements: bartCreatives.assignedPlacements
              })}
            </div>
          )}
        </div>
      </Container>
      <Snackbar
        autoHideDuration={3000}
        open={snackbarOpen}
        onClose={() => setSnackbarOpen(false)}
      >
        <MuiAlert onClose={() => setSnackbarOpen(false)} severity="warning">
          {snackbarMessage}
        </MuiAlert>
      </Snackbar>
    </div>
  );
};;
